// src/features/staff/pages/StaffDetailsPage/StaffDetailsPage.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Alert, CircularProgress } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge as BadgeIcon,
  Folder as FolderIcon,
  Gavel as GavelIcon,
  Security as SecurityIcon,
  TaskAlt as TaskAltIcon,
} from '@mui/icons-material';
import { useNavigationStack } from '../../../../context/NavigationStackContext';

// Subcomponents
import StaffHeader from './components/StaffHeader';
import StaffStamkortTab from './components/StaffStamkortTab';
import StaffTabsNavigation from './components/StaffTabsNavigation';
import TabPanel from './components/TabPanel';
import StaffTaskTracking from './components/StaffTaskTracking';
import StaffOverenskomsterTab from './components/StaffOverenskomsterTab';
import StaffFilesTab from './components/StaffFilesTab';

// External Components
import StaffRolesManager from '../../components/StaffRolesManager';
import UploadProfilePictureDialog from '../../components/UploadProfilePictureDialog';
import ResetPasswordDialog from '../../components/ResetPasswordDialog';

// Hooks
import { useStaff } from '../../hooks/useStaff';
import { staffAPI } from '../../services/staff';
import { useSnackbar } from '../../../../hooks/mui/useSnackBar';
import { usePermissions } from '../../../auth/hooks/usePermissions';

export default function StaffDetailsPage() {
  const { t } = useTranslation(['staff', 'tasks', 'common']);
  const navigate = useNavigate();
  const { navigateBack } = useNavigationStack();
  const { staffId } = useParams();
  const { showError, showSuccess } = useSnackbar();
  const permissions = usePermissions();

  // State
  const [activeTab, setActiveTab] = useState(0);
  const [staffMember, setStaffMember] = useState(null);
  const [staffRoles, setStaffRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);

  // API hooks
  const {
    staff,
    fetchDepartmentStaff,
    updateStaff,
    getStaffRoles,
    error,
    clearError,
  } = useStaff();

  // TODO: Use correct endpoint for fetching staff data
  // Load staff data
  useEffect(() => {
    const loadStaffData = async () => {
      if (!staffId) return;

      setLoading(true);
      try {
        // First fetch all staff to get basic info
        await fetchDepartmentStaff({ include_inactive: true });
        
        // Then fetch roles for this specific staff member
        const rolesResult = await getStaffRoles(staffId);
        if (rolesResult.success) {
          setStaffRoles(rolesResult.data.roles || []);
        }
      } catch (error) {
        showError(t('staff:errors.load_staff_data_failed'));
      } finally {
        setLoading(false);
      }
    };

    loadStaffData();
  }, [staffId, fetchDepartmentStaff, getStaffRoles, showError, t]);

  // Find the current staff member from the loaded staff list
  useEffect(() => {
    if (staff && staffId) {
      const foundStaff = staff.find(s => s.staff_member_id === staffId);
      setStaffMember(foundStaff || null);
    }
  }, [staff, staffId]);

  // Event handlers
  const handleBack = useCallback(() => {
    navigateBack('/staff');
  }, [navigateBack]);

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  const handleStaffUpdate = useCallback(() => {
    // Refresh staff data after updates
    if (staffId) {
      fetchDepartmentStaff({ include_inactive: true });
      getStaffRoles(staffId).then(result => {
        if (result.success) {
          setStaffRoles(result.data.roles || []);
        }
      });
    }
  }, [staffId, fetchDepartmentStaff, getStaffRoles]);

  const handlePasswordReset = useCallback(async (staffId, newPassword) => {
    return await updateStaff(staffId, { password: newPassword });
  }, [updateStaff]);

  const handleUploadSuccess = useCallback((data) => {
    showSuccess(t('staff:profile_picture.success'));
    setUploadDialogOpen(false);
    handleStaffUpdate();
  }, [showSuccess, handleStaffUpdate, t]);

  // Check permissions
  const canAssignRoles = permissions.hasScope('staff:role:assign') && permissions.hasScope('role:read');
  const canUpdateStaff = permissions.hasScope('staff:update');
  const canUploadFiles = permissions.hasScope('file:upload');

  // Staff files live in the staff folder in Filer; its access rules decide
  // whether this person sees them (the tab) and may add to them
  const mayOpenStaffFiles = canUpdateStaff && permissions.hasScope('file:read');
  const [staffFilesAccess, setStaffFilesAccess] = useState(null);
  useEffect(() => {
    if (!mayOpenStaffFiles) return;
    staffAPI.getStaffFilesAccess()
      .then((res) => setStaffFilesAccess(res.data))
      .catch(() => setStaffFilesAccess(null));
  }, [mayOpenStaffFiles]);

  // Task history and agreements come from other modules, so their tabs follow those scopes
  const tabs = [
    { key: 'stamkort', label: t('staff:tabs.stamkort'), icon: <BadgeIcon /> },
    { key: 'roles', label: t('staff:tabs.roles'), icon: <SecurityIcon /> },
    ...(permissions.hasScope('task:read') ? [{ key: 'task_tracking', label: t('staff:tabs.task_tracking'), icon: <TaskAltIcon /> }] : []),
    ...(permissions.hasScope('tidsregistering:overenskomst:read')
      ? [{ key: 'overenskomster', label: t('staff:tabs.overenskomster'), icon: <GavelIcon /> }]
      : []),
    // Contracts are personal: staff:update, file:read and access to the staff folder
    ...(mayOpenStaffFiles && staffFilesAccess?.can_read
      ? [{ key: 'files', label: t('staff:tabs.files'), icon: <FolderIcon /> }]
      : []),
  ];
  const tabIndex = (key) => tabs.findIndex((tab) => tab.key === key);

  // Loading state
  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  // Not found state
  if (!staffMember) {
    return (
      <Box>
        <StaffHeader 
          staffMember={{ name: t('staff:messages.staff_not_found') }} 
          onBack={handleBack}
        />
        <Alert severity="error">
          {t('staff:messages.staff_not_found_description')}
        </Alert>
      </Box>
    );
  }

  return (
    // Sections keep their natural height (no squashing on short windows); only
    // a `fill` tab panel stretches into the space left over
    <Box flex={1} minHeight={0} display="flex" flexDirection="column" sx={{ '& > *': { flexShrink: 0 } }}>
      {/* Header */}
      <StaffHeader staffMember={staffMember} staffRoles={staffRoles} onBack={handleBack} />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* Tabs Navigation */}
      <StaffTabsNavigation tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={tabIndex('stamkort')} fill>
        <StaffStamkortTab
          staffMember={staffMember}
          canUpdate={canUpdateStaff}
          canUploadFiles={canUploadFiles}
          onChangePicture={() => setUploadDialogOpen(true)}
          onResetPassword={() => setResetPasswordDialogOpen(true)}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={tabIndex('roles')} fill>
        {canAssignRoles ? (
          <StaffRolesManager
            staffMember={staffMember}
            staffRoles={staffRoles}
            onUpdate={handleStaffUpdate}
          />
        ) : (
          <Alert severity="info">
            {t('staff:info.permissions_note')}
          </Alert>
        )}
      </TabPanel>

      <TabPanel value={activeTab} index={tabIndex('task_tracking')} fill>
        <StaffTaskTracking staffMember={staffMember} />
      </TabPanel>

      <TabPanel value={activeTab} index={tabIndex('overenskomster')} fill>
        <StaffOverenskomsterTab staffMember={staffMember} />
      </TabPanel>

      <TabPanel value={activeTab} index={tabIndex('files')} fill>
        <StaffFilesTab staffMember={staffMember} canAdd={Boolean(staffFilesAccess?.can_write)} />
      </TabPanel>

      {/* Upload Profile Picture Dialog */}
      <UploadProfilePictureDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        staffMember={staffMember}
        onUploadSuccess={handleUploadSuccess}
      />

      {/* Reset password dialog */}
      <ResetPasswordDialog
        open={resetPasswordDialogOpen}
        onClose={() => setResetPasswordDialogOpen(false)}
        staffMember={staffMember}
        onPasswordReset={handlePasswordReset}
      />
    </Box>
  );
}